<template>
  <div>
    <!-- Mobile Cart Button (Visible on mobile) -->
    <div v-if="isMobile"
      class="mobile-cart fixed z-50 cursor-pointer"
      @click="toggleSidebar">
      <div class="flex items-center">
        <FontAwesomeIcon icon="shopping-cart" class="cart-icon" />
        <div class="cart-info">
          <p v-if="cartStore.state.cart.length">{{ cartStore.state.formatted }}</p>
          <p v-else>{{ $translate('No items in cart') }}</p>
        </div>
      </div>
      <div v-if="cartStore.state.cart.length" class="cart-badge">
        {{ cartStore.state.cart.length }}
      </div>
    </div>

    <!-- Right Sidebar (Visible on desktop, toggle on mobile) -->
    <div class="cart-sidebar fixed right-0 top-0 h-full z-50"
      :style="isMobile && !isSidebarOpen ? { transform: 'translateX(100%)' } : { transform: 'translateX(0)' }">

      <!-- Header -->
      <div class="cart-header">
        <div class="flex items-center justify-between">
          <h3 class="flex items-center font-bold text-xl text-gray-800">
            <FontAwesomeIcon icon="cart-shopping" class="mr-3 text-indigo-500" />
            {{ $translate('Cart Summary') }}
          </h3>
          
          <!-- Close button -->
          <button v-if="isMobile" @click="toggleSidebar"
            class="close-button">
            <FontAwesomeIcon icon="times" />
          </button>
        </div>
      </div>

      <!-- Main Content -->
      <div class="cart-content">
        <!-- Loading State -->
        <div v-if="cartStore.state.isLoading" class="flex justify-center p-8">
          <ApiLoader />
        </div>

        <!-- Error State -->
        <div v-else-if="cartStore.state.error" class="flex flex-col flex-grow justify-center items-center text-red-500 p-8">
          <div class="error-icon-container">
            <font-awesome-icon icon="exclamation-circle" class="error-icon" />
          </div>
          <p class="error-message">{{ cartStore.state.error }}</p>
        </div>

        <!-- Empty Cart State -->
        <div v-else-if="!cartStore.state.cart.length"
          class="flex flex-col flex-grow justify-center items-center text-gray-500 p-8">
          <div class="empty-cart-icon-container">
            <font-awesome-icon icon="shopping-basket" class="empty-cart-icon" />
          </div>
          <p class="empty-cart-message">{{ $translate('Your cart is empty') }}</p>
          <p class="empty-cart-hint">{{ $translate('Add some products to see them here') }}</p>
        </div>

        <!-- Cart Items and Total -->
        <div v-else class="flex flex-col flex-grow overflow-hidden">
          <!-- Cart Items -->
          <div class="cart-items">
            <div v-for="item in cartStore.state.cart" :key="item.id" class="cart-item">
              <CartItem 
                :item="item" 
                @update-quantity="handleUpdateQuantity" 
                @item-removed="cartStore.fetchCartContent"
                @contact-removed="cartStore.fetchCartContent" 
                @voucher-removed="cartStore.fetchCartContent"
                @package-removed="cartStore.fetchCartContent" 
                @open-contact-modal="openContactModal"
                @open-addon-modal="openAddonModal"
                :disabled="isPaymentStep" 
              />
            </div>
          </div>

          <!-- Total Section -->
          <CartTotal :total="cartStore.state.total" />
        </div>
      </div>
    </div>
  </div>

  <!-- Contact Modal and Addon Modal -->
  <ContactModal 
    :isOpen="isContactModalOpen" 
    :itemId="pendingItemId" 
    @submit="handleContactSubmit"
    @close="closeModal" 
  />
  <AddonModal 
    :isOpen="isAddonModalOpen" 
    :itemId="pendingItemId" 
    @submit="handleAddonSubmit" 
    @close="closeModal" 
  />
</template>

<script setup>
import { inject, ref, onMounted, computed } from 'vue';
import CartItem from '@/components/payment/components/CartItem.vue';
import CartTotal from '@/components/payment/components/CartTotal.vue';
import ContactModal from '@/components/modals/ContactModal.vue';
import AddonModal from '@/components/modals/AddonModal.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ApiLoader from '@/components/general/ApiLoader.vue';

const cartStore = inject('cartStore');
const currentStep = inject('currentStep', ref(1)); // Get current step from parent
const isSidebarOpen = ref(false);
const isMobile = ref(false);

// Computed property to check if we're on the payment step
const isPaymentStep = computed(() => currentStep.value === 4);

// Modal states
const isContactModalOpen = ref(false);
const isAddonModalOpen = ref(false);
const pendingItemId = ref(null);

// Toggle sidebar visibility
const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value;
};

// Detect mobile screen size
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 1447; // Adjust breakpoint for mobile
};

// Methods for quantity, contact, and addon handling
const handleUpdateQuantity = async (itemId, quantity) => {
  console.log('handleUpdateQuantity');
  await cartStore.updateQuantity(itemId, quantity);
  if (cartStore.state.contactRequired && !isContactModalOpen.value) {
    pendingItemId.value = itemId;
    isContactModalOpen.value = true;
  }
};

const handleContactSubmit = async (contact) => {
  console.log('handleContactSubmit');
  if (pendingItemId.value) {
    let cartItem = cartStore.state.cart.find((item) => item.id === pendingItemId.value);
    let quantity = cartItem ? cartItem.quantity : 1;
    await cartStore.updateQuantity(pendingItemId.value, quantity, contact);
    isContactModalOpen.value = false;
    pendingItemId.value = null;
  }
};

const handleAddonSubmit = async (addon) => {
  console.log('handleAddonSubmit');
  if (pendingItemId.value) {
    await cartStore.addAddon(pendingItemId.value, addon);
    isAddonModalOpen.value = false;
    pendingItemId.value = null;
  }
};

const closeModal = () => {
  isContactModalOpen.value = false;
  isAddonModalOpen.value = false;
  pendingItemId.value = null;
};

const openContactModal = (itemId) => {
  pendingItemId.value = itemId;
  isContactModalOpen.value = true;
  isAddonModalOpen.value = false;
};

const openAddonModal = (itemId) => {
  pendingItemId.value = itemId;
  isAddonModalOpen.value = true;
  isContactModalOpen.value = false;
};


onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});
</script>

<style scoped>
.cart-sidebar {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 400px;
  box-shadow: -3px 0 20px rgba(0, 0, 0, 0.08);
  background-color: white;
  border-left: 1px solid #f3f4f6;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.cart-header {
  background-color: #f9fafb;
  border-bottom: 1px solid #f3f4f6;
  padding: 1.5rem;
}

.cart-header h3 {
  font-size: 1.5rem;
  margin: 0;
}

.close-button {
  width: 2.5rem;
  height: 2.5rem;
  background-color: #f3f4f6;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1.25rem;
}

.close-button:hover {
  background-color: #e5e7eb;
  color: #111827;
  transform: rotate(90deg);
}

.cart-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cart-items {
  flex-grow: 1;
  overflow-y: auto;
  padding: 1.25rem;
  /* Add custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #d1d5db #f3f4f6;
}

.cart-items::-webkit-scrollbar {
  width: 6px;
}

.cart-items::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 3px;
}

.cart-items::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 3px;
}

.cart-item {
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f3f4f6;
}

.cart-item:last-child {
  border-bottom: none;
}

/* Mobile cart icon */
.mobile-cart {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #6366f1;
  color: white;
  border-radius: 0.75rem;
  padding: 0.875rem 1.25rem;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
  transition: all 0.3s ease;
  bottom: 1.5rem;
  right: 1.5rem;
  position: fixed;
  min-width: 200px;
}

.mobile-cart:hover {
  background-color: #4f46e5;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(99, 102, 241, 0.3);
}

.cart-icon {
  font-size: 1.25rem;
  margin-right: 0.75rem;
}

.cart-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  background-color: white;
  color: #6366f1;
  font-weight: 600;
  font-size: 0.875rem;
  border-radius: 9999px;
  margin-left: 0.75rem;
  padding: 0 0.375rem;
}

/* Empty cart styling */
.empty-cart-icon-container {
  width: 5rem;
  height: 5rem;
  background-color: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.empty-cart-icon {
  font-size: 2.5rem;
  color: #9ca3af;
}

.empty-cart-message {
  font-size: 1.25rem;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 0.5rem;
}

.empty-cart-hint {
  font-size: 0.875rem;
  color: #6b7280;
}

/* Error state styling */
.error-icon-container {
  width: 5rem;
  height: 5rem;
  background-color: #fee2e2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.error-icon {
  font-size: 2.5rem;
  color: #ef4444;
}

.error-message {
  font-size: 1.125rem;
  color: #ef4444;
  text-align: center;
  max-width: 300px;
}

/* Responsive adjustments */
@media (max-width: 1447px) {
  .cart-sidebar {
    width: 90%;
    max-width: 400px;
  }
}

@media (max-width: 768px) {
  .mobile-cart {
    min-width: auto;
  }
  
  .cart-info {
    display: none;
  }
}

@media (max-width: 640px) {
  .cart-sidebar {
    width: 100%;
    max-width: none;
  }
  
  .cart-header {
    padding: 1.25rem;
  }
  
  .mobile-cart {
    bottom: 1rem;
    right: 1rem;
    padding: 0.75rem;
  }
}
</style>
